import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  MessageCircle, X, Send, Sparkles, ArrowUpRight, Loader2, AlertCircle, ShieldAlert,
} from "lucide-react";
import { api, formatApiError } from "../services/api";

// ─── Markdown-link renderer (safe subset: only [label](/internal) & [label](https://…)) ───
// Parse le contenu et transforme les liens Markdown en <Link> (interne) ou <a target="_blank"> (externe).
// Aucune autre balise Markdown n'est interprétée : sécurisé par construction.
const MD_LINK_RE = /\[([^\]]+)\]\((\/[^)\s]*|https?:\/\/[^)\s]+)\)/g;
function renderContentWithLinks(text, onNavigate) {
  if (!text || typeof text !== "string") return text;
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  MD_LINK_RE.lastIndex = 0;
  while ((match = MD_LINK_RE.exec(text)) !== null) {
    const [full, label, href] = match;
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (href.startsWith("/")) {
      nodes.push(
        <Link
          key={`ml-${key++}`}
          to={href}
          onClick={onNavigate}
          className="afr-assistant__inline-link"
          data-testid={`welcome-assistant-inline-link-${href.replace(/[^a-z0-9]+/gi, "-")}`}
        >
          {label}
        </Link>
      );
    } else {
      nodes.push(
        <a
          key={`ml-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="afr-assistant__inline-link"
        >
          {label}
        </a>
      );
    }
    lastIndex = match.index + full.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.length ? nodes : text;
}

const WELCOME = {
  role: "assistant",
  content:
    "Bonjour et bienvenue sur AFRICTAL 👋\n" +
    "Je suis votre Assistant AFRICTAL. Je suis là pour vous aider à découvrir la plateforme, " +
    "trouver des opportunités, rechercher des talents, répondre à vos questions et vous " +
    "orienter vers les services les plus adaptés à vos besoins.\n\n" +
    "Dites-moi simplement ce que vous souhaitez accomplir aujourd'hui.",
  category: "faq",
  suggestions: [
    { label: "Je cherche un emploi", href: "/offers" },
    { label: "Je recrute un talent", href: "/talents" },
    { label: "Je veux me former", href: "/formations" },
    { label: "Découvrir AFRICTAL", href: "/ecosystemes" },
  ],
};

const QUICK_PROMPTS = [
  "Comment fonctionne AFRICTAL ?",
  "Je cherche des développeurs Java au Sénégal",
  "Je suis étudiant, comment m'inscrire ?",
  "Je veux valoriser mon parcours",
];

const CATEGORY_LABEL = {
  navigation: "Navigation",
  faq: "FAQ",
  info_search: "Info",
  resource_search: "Ressources",
  strategic: "Accompagnement",
};

const CATEGORY_TINT = {
  navigation: "var(--afr-primary)",
  faq: "var(--afr-accent)",
  info_search: "var(--mod-freelance)",
  resource_search: "var(--mod-talent)",
  strategic: "var(--afr-danger, #d64545)",
};


export default function WelcomeAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState({});
  const [orchestratorLink, setOrchestratorLink] = useState(null); // { role, href, count }
  const [sessionId] = useState(() => {
    // Persist session id across page navigations for a single visitor
    try {
      const existing = window.sessionStorage.getItem("africtal_assistant_sid");
      if (existing) return existing;
      const fresh = Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.sessionStorage.setItem("africtal_assistant_sid", fresh);
      return fresh;
    } catch {
      return Math.random().toString(36).slice(2);
    }
  });
  const feedRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  // Auto-open once per session, 5 s after landing on the home page.
  // sessionStorage flag ensures we never re-trigger during the same visit.
  useEffect(() => {
    if (location.pathname !== "/") return;
    try {
      if (window.sessionStorage.getItem("africtal_assistant_autoopened") === "1") return;
    } catch { /* private mode: fall through */ }
    const timer = setTimeout(() => {
      setOpen(true);
      try { window.sessionStorage.setItem("africtal_assistant_autoopened", "1"); } catch { /* ignore */ }
    }, 5000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  // When authenticated, discover if a personal or admin orchestrator link should be shown.
  useEffect(() => {
    if (!open) return;
    // The api client auto-sends the httpOnly auth cookie; if unauthenticated,
    // requests will simply 401 and we silently skip.
    const check = async () => {
      try {
        // Admin? Try the pending list first — 403/401 means non-admin/anonymous.
        const admRes = await api.get("/africtal-orchestrator/pending");
        if ((admRes.data?.total || 0) > 0) {
          setOrchestratorLink({ role: "admin", href: "/admin/orchestrator", count: admRes.data.total });
          return;
        }
      } catch {
        // Not admin or not logged — silent, fall through to user lookup.
      }
      try {
        const myRes = await api.get("/africtal-orchestrator/my");
        if ((myRes.data?.total || 0) > 0) {
          setOrchestratorLink({ role: "user", href: "/mon-accompagnement", count: myRes.data.total });
        }
      } catch {
        // Not logged in — silent
      }
    };
    check();
  }, [open]);

  // V3.18 · Proactive Lab suggestion for talents in "Parcours d'Insertion"
  useEffect(() => {
    if (!open) return;
    const check = async () => {
      try {
        const { data } = await api.get("/v1/insertion/me");
        if (!data?.eligible) return;
        // Don't inject twice per session
        if (window.sessionStorage.getItem("africtal_lab_prompt_shown") === "1") return;
        const gap = data.gap_months ? ` Cela fait ${data.gap_months} mois depuis votre diplôme.` : "";
        const proactive = {
          role: "assistant",
          category: "insertion",
          content:
            `Je vois que vous êtes dans le Parcours d'Insertion (${data.situation_label || "profil éligible"}).${gap} ` +
            "Pour briser cette période sans exercice, je vous recommande **fortement** un défi rapide du Lab — c'est le moyen le plus court pour démontrer une compétence et enrichir votre Passeport.",
          actions: [
            { label: "Ouvrir Le Lab", href: "/lab" },
            { label: "Voir mon profil", href: "/profile" },
          ],
        };
        setMessages((prev) => [...prev, proactive]);
        try { window.sessionStorage.setItem("africtal_lab_prompt_shown", "1"); } catch { /* ignore */ }
      } catch {
        // Silent — anonymous or non-talent
      }
    };
    check();
  }, [open]);

  const send = useCallback(async (text) => {
    const clean = (text || "").trim();
    if (!clean || loading) return;

    const userMsg = { role: "user", content: clean };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setInput("");
    setLoading(true);

    // Only send last 8 messages as history — assistant should stay concise
    const history = nextHistory
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-8)
      .map(({ role, content }) => ({ role, content }));

    try {
      const { data } = await api.post("/assistant/chat", {
        session_id: sessionId, message: clean, history,
      });
      if (data.context) setContext(data.context);
      const botMsg = {
        role: "assistant",
        content: data.reply || "Je n'ai pas de réponse pour l'instant.",
        category: data.category,
        actions: data.actions || [],
        resources: data.resources || [],
        escalated: data.escalated,
      };
      setMessages((m) => [...m, botMsg]);
    } catch (e) {
      console.warn("[assistant] chat failed:", formatApiError(e));
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Une erreur temporaire est survenue. Merci de réessayer dans un instant.",
          category: "faq",
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [messages, loading, sessionId]);

  const submit = (e) => {
    e.preventDefault();
    send(input);
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="welcome-assistant-open"
          aria-label="Ouvrir l'Assistant d'Accueil"
          className="afr-assistant-fab"
        >
          <Sparkles size={16} aria-hidden="true" />
          <span>Assistant AFRICTAL</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <section
          className="afr-assistant"
          role="dialog"
          aria-label="Assistant d'Accueil AFRICTAL"
          data-testid="welcome-assistant-panel"
        >
          <header className="afr-assistant__head">
            <div className="afr-assistant__brand">
              <div className="afr-assistant__badge" aria-hidden="true">
                <MessageCircle size={16} />
              </div>
              <div className="afr-assistant__title">
                <strong>Assistant AFRICTAL</strong>
                <span>Ici pour vous accueillir</span>
              </div>
            </div>
            <button
              type="button"
              className="afr-assistant__close"
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              data-testid="welcome-assistant-close"
            >
              <X size={16} />
            </button>
          </header>

          {orchestratorLink && (
            <Link
              to={orchestratorLink.href}
              onClick={() => setOpen(false)}
              className="afr-assistant__orch-link"
              data-testid="welcome-assistant-orch-link"
            >
              <Sparkles size={13} />
              {orchestratorLink.role === "admin"
                ? <>Contrôle Orchestrateur · <strong>{orchestratorLink.count}</strong> en attente</>
                : <>Mon accompagnement AFRICTAL</>}
              <ArrowUpRight size={12} />
            </Link>
          )}

          {(context.profile || context.sector || context.country || context.project) && (
            <div className="afr-assistant__ctx" data-testid="welcome-assistant-context">
              <span className="afr-assistant__ctx-label">Contexte de la conversation :</span>
              {context.profile && <span className="afr-assistant__ctx-chip">{context.profile}</span>}
              {context.sector && <span className="afr-assistant__ctx-chip">{context.sector}</span>}
              {context.country && <span className="afr-assistant__ctx-chip">{context.country}</span>}
              {context.project && (
                <span className="afr-assistant__ctx-chip" title={context.project}>
                  {String(context.project).slice(0, 30)}
                  {String(context.project).length > 30 ? "…" : ""}
                </span>
              )}
            </div>
          )}

          <div className="afr-assistant__feed" ref={feedRef} data-testid="welcome-assistant-feed">
            {messages.map((m, i) => (
              <article
                key={i}
                className={`afr-assistant__msg afr-assistant__msg--${m.role}`}
                data-testid={`welcome-assistant-msg-${i}`}
              >
                {m.role === "assistant" && m.category && (
                  <span
                    className="afr-assistant__cat"
                    style={{ background: CATEGORY_TINT[m.category] || "var(--afr-primary)" }}
                  >
                    {m.escalated ? <ShieldAlert size={11} /> : <Sparkles size={11} />}
                    {CATEGORY_LABEL[m.category] || m.category}
                  </span>
                )}
                <p className={m.error ? "afr-assistant__err" : ""} style={{ whiteSpace: "pre-wrap" }}>
                  {m.error && <AlertCircle size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />}
                  {m.role === "assistant" ? renderContentWithLinks(m.content, () => setOpen(false)) : m.content}
                </p>

                {m.role === "assistant" && Array.isArray(m.actions) && m.actions.length > 0 && (
                  <div className="afr-assistant__actions" data-testid={`welcome-assistant-actions-${i}`}>
                    {m.actions.map((a, j) => (
                      <Link
                        key={j}
                        to={a.href || "/"}
                        onClick={() => setOpen(false)}
                        className="afr-assistant__action"
                        data-testid={`welcome-assistant-action-${i}-${j}`}
                      >
                        {a.label} <ArrowUpRight size={12} />
                      </Link>
                    ))}
                  </div>
                )}

                {m.role === "assistant" && Array.isArray(m.resources) && m.resources.length > 0 && (
                  <ul className="afr-assistant__resources" data-testid={`welcome-assistant-resources-${i}`}>
                    {m.resources.slice(0, 5).map((r, k) => (
                      <li key={k}>
                        <strong>{r.title || r.name || "(sans titre)"}</strong>
                        {r.location && <span> · {r.location}</span>}
                        {r.role && <span> · {r.role}</span>}
                        {r.provider && <span> · {r.provider}</span>}
                      </li>
                    ))}
                  </ul>
                )}

                {m.role === "assistant" && i === 0 && Array.isArray(m.suggestions) && (
                  <div className="afr-assistant__actions">
                    {m.suggestions.map((s, j) => (
                      <Link
                        key={j}
                        to={s.href}
                        onClick={() => setOpen(false)}
                        className="afr-assistant__action"
                        data-testid={`welcome-assistant-suggestion-${j}`}
                      >
                        {s.label} <ArrowUpRight size={12} />
                      </Link>
                    ))}
                  </div>
                )}
              </article>
            ))}

            {loading && (
              <article className="afr-assistant__msg afr-assistant__msg--assistant" data-testid="welcome-assistant-loading">
                <p><Loader2 size={13} className="afr-spin" style={{ verticalAlign: "middle", marginRight: 6 }} /> Recherche en cours…</p>
              </article>
            )}
          </div>

          {messages.length <= 2 && (
            <div className="afr-assistant__quick" data-testid="welcome-assistant-quick">
              {QUICK_PROMPTS.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="afr-assistant__chip"
                  onClick={() => send(q)}
                  disabled={loading}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="afr-assistant__form" data-testid="welcome-assistant-form">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez votre question à l'Assistant…"
              maxLength={500}
              disabled={loading}
              data-testid="welcome-assistant-input"
              aria-label="Votre message"
            />
            <button
              type="submit"
              className="afr-assistant__send"
              disabled={loading || !input.trim()}
              data-testid="welcome-assistant-send"
              aria-label="Envoyer"
            >
              {loading ? <Loader2 size={14} className="afr-spin" /> : <Send size={14} />}
            </button>
          </form>
        </section>
      )}
    </>
  );
}
